import type { InboxNotification } from "@/api/endpoints/notifications"

function extractId(value: unknown): string | null {
  if (value == null) return null
  const id = String(value).trim()
  return id.length > 0 ? id : null
}

export function extractAppointmentIdFromNotification(
  notification: InboxNotification,
): string | null {
  const data = notification.data
  if (!data || typeof data !== "object") return null

  const nested = (data as Record<string, unknown>).data
  if (nested && typeof nested === "object") {
    const map = nested as Record<string, unknown>
    return (
      extractId(map._id) ??
      extractId(map.id) ??
      extractId(map.appointmentId)
    )
  }

  const record = data as Record<string, unknown>
  return (
    extractId(record._id) ??
    extractId(record.id) ??
    extractId(record.appointmentId)
  )
}

export function extractCampaignIdFromNotification(
  notification: InboxNotification,
): string | null {
  const data = notification.data
  if (!data || typeof data !== "object") return null

  const record = data as Record<string, unknown>
  return (
    extractId(record.campaignId) ??
    extractId(record.adCampaignId) ??
    extractId(record.advertisementCampaign)
  )
}

function isAdminContext(): boolean {
  return typeof window !== "undefined" && window.location.pathname.startsWith("/admin")
}

export function resolveNotificationPath(
  notification: InboxNotification,
): string | null {
  const admin = isAdminContext()

  if (notification.type === "SLOT_REQUEST_CREATED") {
    return admin ? null : "/slot-requests"
  }

  const appointmentId = extractAppointmentIdFromNotification(notification)
  if (appointmentId) {
    return admin ? `/admin/appointments/${appointmentId}` : `/appointments/${appointmentId}`
  }

  if (notification.type?.startsWith("AD_CAMPAIGN")) {
    const campaignId = extractCampaignIdFromNotification(notification)
    if (campaignId) {
      return admin
        ? `/admin/ad-campaigns/${campaignId}`
        : `/ad-campaigns/${campaignId}`
    }
    return admin ? "/admin/ad-campaigns" : "/ad-campaigns"
  }

  if (notification.type?.startsWith("PAYOUT")) {
    return admin ? "/admin/payouts" : "/finance"
  }

  if (notification.type === "REVIEW_RECEIVED") {
    return admin ? "/admin/services" : "/services"
  }

  return null
}
